// Phone number helpers. All persisted phone numbers are stored in E.164 format
// (https://en.wikipedia.org/wiki/E.164), e.g. "+4520123456".
//
// Parsing and validation is delegated to libphonenumber-js. The module is
// isomorphic — it is imported both from `"use client"` wizards and from API
// routes — so it must stay free of `node:*` imports and `server-only`. That is
// why we import from `libphonenumber-js/core`: the `/core` entrypoint ships the
// algorithms without any bundled metadata, and we hand it our own metadata.
//
// `phone-metadata.dk.json` is a GENERATED DK-only slice (182 bytes) — regenerate
// with `generate:phone-metadata` when Google updates their numbering plan. The
// generator is intentionally run WITHOUT `--with-phone-number-types`, so the
// slice carries the numbering plan's outer envelope (`[2-9]\d{7}`) but not the
// per-range subscriber patterns. See decisions/leads.md (2026-08-01).

import {
  getCountryCallingCode,
  Metadata,
  type MetadataJson,
  parsePhoneNumberFromString,
} from "libphonenumber-js/core";
import metadataJson from "@/shared/lib/phone-metadata.dk.json";

// The generated JSON is minified into positional arrays, which TypeScript infers
// as a union of tuple shapes rather than libphonenumber's `MetadataJson`.
const METADATA = metadataJson as unknown as MetadataJson;

export type CountryCode = "DK";

export const DEFAULT_COUNTRY: CountryCode = "DK";

function numberingPlan(country: CountryCode) {
  const metadata = new Metadata(METADATA);
  metadata.selectNumberingPlan(country);
  const plan = metadata.numberingPlan;
  if (!plan) throw new Error(`phone metadata mangler numbering plan for ${country}`);
  return plan;
}

const DIAL_CODE: Record<CountryCode, string> = {
  DK: getCountryCallingCode(DEFAULT_COUNTRY, METADATA),
};

// DK has a single possible length (8). Kept as a set so the module keeps working
// if Google ever adds another length to the plan.
const NATIONAL_LENGTHS: Record<CountryCode, Set<number>> = {
  DK: new Set(numberingPlan(DEFAULT_COUNTRY).possibleLengths()),
};

/**
 * Normalize a user-entered phone string to E.164. Accepts raw national digits
 * ("20123456"), spaced/dashed forms ("20 12 34 56"), or international form with
 * or without plus ("+4520123456", "4520123456", "004520123456"). Returns null
 * for input that cannot be parsed as a number in the given country's numbering
 * plan.
 *
 * `extract: false` keeps this a phone *field* parser rather than a text scanner:
 * "ring til mig på 20123456" is a mistake in a phone field, not a phone number.
 */
export function toE164(
  input: string | null | undefined,
  country: CountryCode = DEFAULT_COUNTRY,
): string | null {
  if (!input) return null;
  const trimmed = String(input).trim();
  if (!trimmed) return null;

  // libphonenumber prefers the national interpretation, so 8-digit DK numbers
  // starting with "45" ("45123456" → "+4545123456") aren't mis-parsed as country
  // code + 6 digits, while a full "4520123456" still yields "+4520123456".
  const parsed = parsePhoneNumberFromString(
    trimmed,
    { defaultCountry: country, extract: false },
    METADATA,
  );
  if (!parsed || parsed.country !== country) return null;

  return parsed.isValid() ? parsed.number : null;
}

/** E.164 digits without the leading "+", for hashing or systems that reject "+". */
export function toE164Digits(
  input: string | null | undefined,
  country: CountryCode = DEFAULT_COUNTRY,
): string | null {
  const e164 = toE164(input, country);
  return e164 ? e164.slice(1) : null;
}

/**
 * True if `input` (after stripping whitespace) is a bare national number that
 * exists in the DK numbering plan. Deliberately national-only: the wizards feed
 * this the digits from `<PhoneInput>`, which already owns the "+45" prefix, so
 * "+4520123456" is rejected here while `toE164` accepts it.
 */
export function isValidDanishNational(input: string | null | undefined): boolean {
  const compact = String(input ?? "").replace(/\s/g, "");
  if (!/^\d+$/.test(compact)) return false;
  if (!NATIONAL_LENGTHS[DEFAULT_COUNTRY].has(compact.length)) return false;
  return toE164(compact) !== null;
}

/**
 * Extract the best national-digits guess from a free-form phone input. Designed
 * for use in `onChange` handlers on controlled inputs: accepts autofill/paste
 * formats like "+4520123456" or "004520123456" (password managers often emit
 * these) and returns "20123456" — without losing the last two digits. Falls
 * back to truncating the first N digits for plain input.
 *
 * Hand-rolled on purpose: this runs on every keystroke over half-typed input, so
 * libphonenumber's parser (which only sees complete numbers) is the wrong tool.
 */
export function extractNationalDigits(
  input: string,
  country: CountryCode = DEFAULT_COUNTRY,
): string {
  const dialCode = DIAL_CODE[country];
  const nationalLength = Math.max(...NATIONAL_LENGTHS[country]);
  const raw = String(input ?? "");
  const digits = raw.replace(/\D/g, "");
  const intlLength = dialCode.length + nationalLength;

  if (
    raw.trimStart().startsWith("+") &&
    digits.length >= intlLength &&
    digits.startsWith(dialCode)
  ) {
    return digits.slice(dialCode.length, intlLength);
  }
  if (
    raw.trimStart().startsWith("00") &&
    digits.length >= 2 + intlLength &&
    digits.startsWith(`00${dialCode}`)
  ) {
    return digits.slice(2 + dialCode.length, 2 + intlLength);
  }
  return digits.slice(0, nationalLength);
}
